import React, { useMemo } from 'react';
import classNames from 'classnames';
import ReportDetails from 'components/ReportDetails';
import ResolvePopup from 'components/ResolvePopup';
import {
  REPORT_ACTION_TRANSLATION,
  REPORT_ACTION_ICONS,
  FILE_UPLOAD_FAILED_COPY,
  LOG_IN_SOURCE_TRANSLATION,
} from '../../utils';
import {
  DataRoomReport,
  ERROR_REPORT_ACTIONS,
  REPORT_ACTION_TYPE,
} from '../../types';
import { useTranslation } from 'react-i18next';
import { MAX_SIZE_COPY } from 'router/subrouters/DataRoom/components/DataRoomDocumentsRouter/data/DataTypes';
import TransWithNda from 'components/TransWithNda';
import {
  getArrayLength,
  getJoinedWordsWithComma,
  getUserFullNameWithEmail,
} from 'utils';
import {
  DATE_FORMAT_COMPACT_SLASH,
  DATE_FORMAT_COMPACT_SLASH_WITH_TIME,
} from 'dateUtils';

import './ReportLog.styles.scss';

type ReportLogProps = {
  className?: string;
  report: DataRoomReport;
  isSummarised?: boolean;
};

const ReportLog: React.FC<ReportLogProps> = (props) => {
  const { className, report, isSummarised } = props;
  const {
    action,
    createdAt,
    count,
    memberFirstName,
    memberLastName,
    memberEmail,
    oldDocumentName,
    changedAccessMemberFirstName,
    changedAccessMemberLastName,
    changedAccessMemberEmail,
    changedAccessMemberRole,
    timeSpent,
    documentNames,
    shareableLinkNames,
    rfiNames,
    memberIpAddress,
    fileUploadErrorCode,
    isMemberVerified,
    envelopeTemplateName,
    loginSource,
  } = report;

  const { t } = useTranslation();

  const classes = classNames('report-log', className, {
    error: ERROR_REPORT_ACTIONS.includes(action),
  });

  const listOfElements = useMemo(
    () => [
      ...(documentNames ?? []),
      ...(rfiNames ?? []),
      ...(shareableLinkNames ?? []),
    ],
    [documentNames, rfiNames, shareableLinkNames],
  );

  const listLength = getArrayLength(listOfElements);

  const itemsCount = count || listLength || 1;

  const popupContent = useMemo(
    () => (
      <div className="resolve-popup-ul">
        {listOfElements.map((item) => (
          <div key={item}>{item}</div>
        ))}
      </div>
    ),
    [listOfElements],
  );

  // A single log in row reads differently depending on where the member came
  // from. Summarised rows roll several logins up under one count and stay on the
  // plain wording, as do rows recorded before sources were tracked and any
  // source we don't recognise.
  const i18nKey = useMemo(
    () =>
      (action === REPORT_ACTION_TYPE.LOG_IN &&
        itemsCount === 1 &&
        loginSource &&
        LOG_IN_SOURCE_TRANSLATION[loginSource]) ||
      REPORT_ACTION_TRANSLATION[action],
    [action, itemsCount, loginSource],
  );

  const text = (
    <TransWithNda
      isParagraph={false}
      i18nKey={i18nKey}
      envelopeTemplateName={envelopeTemplateName}
      values={{
        count: itemsCount,
        memberName: getUserFullNameWithEmail(
          memberFirstName,
          memberLastName,
          memberEmail,
        ),
        memberEmail,
        oldDocumentName,
        changedAccessMemberName: getUserFullNameWithEmail(
          changedAccessMemberFirstName,
          changedAccessMemberLastName,
          changedAccessMemberEmail,
        ),
        changedAccessMemberRole,
        timeSpent,
        memberIpAddress,
        documentName: getJoinedWordsWithComma(documentNames),
        rfiName: getJoinedWordsWithComma(rfiNames),
        shareableLinkName: getJoinedWordsWithComma(shareableLinkNames),
        notVerified: isMemberVerified ? '' : t('notVerifiedInBrackets'),
        errorType: t(FILE_UPLOAD_FAILED_COPY[fileUploadErrorCode], {
          fileLimit: MAX_SIZE_COPY,
        }),
      }}
      {...(listLength && {
        components: {
          popup: <ResolvePopup onHover popupContent={popupContent} />,
        },
      })}
    />
  );

  return (
    <ReportDetails
      className={classes}
      text={text}
      createdAt={createdAt}
      icon={REPORT_ACTION_ICONS[action]}
      dateFormat={
        isSummarised
          ? DATE_FORMAT_COMPACT_SLASH
          : DATE_FORMAT_COMPACT_SLASH_WITH_TIME
      }
    />
  );
};

export default ReportLog;
